"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";

import Chat from "@/components/ai/chat/chat";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { AGENT_NAMES } from "@/lib/constants/agents";

type ChatViewProps = {
  children: React.ReactNode;
  selectedView: string;
};

const SPLIT_CHAT_AUTO_SAVE_ID = "sequelizer-split-chat-layout";

/** Default split: main 75% / chat 25% — chat cannot be dragged narrower than its initial share. */
const SPLIT_MAIN_DEFAULT_PCT = 75;
const SPLIT_CHAT_DEFAULT_PCT = 25;
/** Main pane floor when chat is dragged to maximum width (small app strip beside left nav). */
const SPLIT_MAIN_MIN_PCT = 5;
/** Chat may grow to fill the rest of the main content area (100 − main min). */
const SPLIT_CHAT_MAX_PCT = 100 - SPLIT_MAIN_MIN_PCT;

/**
 * Left pane body keeps the default main share of the split container width
 * (`SPLIT_MAIN_DEFAULT_PCT`) so the page layout (e.g. project table) can sit
 * under the chat (slide-over). Header chrome should use
 * `--main-visible-width` so controls stay in the visible strip beside chat.
 * Literal class so Tailwind JIT keeps it — must stay in sync with SPLIT_MAIN_DEFAULT_PCT.
 */
const SPLIT_MAIN_CONTENT_MIN_WIDTH_CLASS = "min-w-[75cqw]";

const ChatView = React.memo<ChatViewProps>(({ children, selectedView }) => {
  const isMobile = useIsMobile();
  // Below md, never render the resizable split — full-screen chat only.
  const effectiveView =
    isMobile && selectedView === "split-view" ? "chat" : selectedView;
  const isChatOnly = effectiveView === "chat";
  const isSplitView = effectiveView === "split-view";
  const isAppView = effectiveView === "app";
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const [mainVisibleWidthPx, setMainVisibleWidthPx] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!isSplitView) {
      setMainVisibleWidthPx(null);
      return;
    }

    const node = mainScrollRef.current;
    if (!node) {
      return;
    }

    const updateWidth = (): void => {
      setMainVisibleWidthPx(node.clientWidth);
    };
    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(node);
    return (): void => {
      observer.disconnect();
    };
  }, [isSplitView]);

  if (isSplitView) {
    return (
      <div className="@container relative flex h-full min-h-0 w-full">
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId={SPLIT_CHAT_AUTO_SAVE_ID}
          className="flex h-full min-h-0 w-full"
        >
          <ResizablePanel
            defaultSize={SPLIT_MAIN_DEFAULT_PCT}
            minSize={SPLIT_MAIN_MIN_PCT}
            className="min-h-0 min-w-0"
          >
            <div
              ref={mainScrollRef}
              className="h-full min-h-0 w-full overflow-y-auto overflow-x-hidden"
              style={
                mainVisibleWidthPx != null
                  ? ({
                      ["--main-visible-width" as string]: `${mainVisibleWidthPx}px`,
                    } as React.CSSProperties)
                  : undefined
              }
            >
              <div
                className={`min-h-full w-full ${SPLIT_MAIN_CONTENT_MIN_WIDTH_CLASS}`}
              >
                {children}
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle
            className="w-1.5 shrink-0 cursor-col-resize bg-border/60 transition-colors hover:bg-border data-[panel-group-direction=horizontal]:w-1.5"
            aria-label="Resize chat panel"
          />
          <ResizablePanel
            defaultSize={SPLIT_CHAT_DEFAULT_PCT}
            minSize={SPLIT_CHAT_DEFAULT_PCT}
            maxSize={SPLIT_CHAT_MAX_PCT}
            className="relative z-20 min-h-0 min-w-0 bg-background"
          >
            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
              <Chat agentName={AGENT_NAMES.SEQUELIZER} />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 overflow-hidden">
      <motion.div
        className={`min-h-0 min-w-0 overflow-hidden ${isChatOnly ? "w-0" : "w-full"}`}
        animate={{
          width: isChatOnly ? "0%" : "100%",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <motion.div
          className="flex h-full min-h-0 w-full min-w-0 max-w-full overflow-y-auto overflow-x-hidden"
          initial={{ opacity: 0, x: -20 }}
          animate={{
            opacity: isAppView ? 1 : 0,
            x: isAppView ? 0 : -20,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {children}
        </motion.div>
      </motion.div>

      {/* Opaque bg (no backdrop-blur): blur over a scrolling viewport flashes. */}
      <motion.div
        className={`min-h-0 overflow-hidden transition-colors duration-300 ease-in-out ${
          isAppView ? "w-0" : "w-full bg-background"
        }`}
        animate={{
          width: isAppView ? "0%" : "100%",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <motion.div
          className="h-full min-h-0"
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: isChatOnly ? 1 : 0,
            x: isChatOnly ? 0 : 20,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <Chat agentName={AGENT_NAMES.SEQUELIZER} />
        </motion.div>
      </motion.div>
    </div>
  );
});

ChatView.displayName = "ChatView";

export default ChatView;
